import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Phone, UserRound, Wallet } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, tl, todayStr, monthStr, shortDate } from "../lib/api";
import { useBusiness } from "../context/BusinessContext";
import { PageHeader, card, btnPrimary } from "../components/PageHeader";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const num = (v) => parseFloat(String(v).replace(",", "."));
const FIELDS = [["first_name", "Adı"], ["last_name", "Soyadı"], ["phone", "Telefon", "tel"], ["salary", "Maaş (₺)"]];

function StaffDialog({ open, initial, onClose, onSave }) {
  const [f, setF] = useState({});
  useEffect(() => {
    setF(initial || {});
  }, [initial, open]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="staff-dialog">
        <DialogHeader><DialogTitle className="font-heading">{initial ? "Personeli Düzenle" : "Yeni Personel"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          {FIELDS.map(([k, l, t]) => (
            <div key={k}>
              <Label className="text-xs">{l}</Label>
              <Input data-testid={`staff-${k}-input`} type={t || "text"} inputMode={k === "salary" ? "decimal" : undefined} value={f[k] ?? ""}
                onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1 h-10 rounded-xl" />
            </div>
          ))}
        </div>
        <button data-testid="staff-save-button" className={btnPrimary}
          onClick={() => onSave({ first_name: f.first_name || "", last_name: f.last_name || "", phone: f.phone || "", salary: num(f.salary || 0) || 0 })}>
          Kaydet
        </button>
      </DialogContent>
    </Dialog>
  );
}

function PaymentForm({ onAdd }) {
  const [f, setF] = useState({ pay_type: "avans", amount: "", date: todayStr(), note: "" });
  const submit = async (e) => {
    e.preventDefault();
    if (await onAdd({ ...f, amount: num(f.amount) })) setF({ ...f, amount: "", note: "" });
  };
  return (
    <form onSubmit={submit} className="space-y-2 rounded-xl border border-dashed border-cyan-300 bg-cyan-50/50 p-3" data-testid="staff-payment-form">
      <div className="text-xs font-semibold text-cyan-900">Kasa dışı ödeme ekle (güne dahil edilmez)</div>
      <div className="grid grid-cols-2 gap-2">
        <select data-testid="staff-payment-type-select" value={f.pay_type} onChange={(e) => setF({ ...f, pay_type: e.target.value })} className="h-10 rounded-xl border border-slate-200 bg-white px-2 text-sm">
          <option value="avans">Avans</option>
          <option value="maas">Maaş</option>
        </select>
        <Input data-testid="staff-payment-date-input" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="h-10 rounded-xl bg-white" />
        <Input data-testid="staff-payment-amount-input" required inputMode="decimal" placeholder="Tutar (₺)" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className="h-10 rounded-xl bg-white font-mono" />
        <Input data-testid="staff-payment-note-input" placeholder="Açıklama" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className="h-10 rounded-xl bg-white" />
      </div>
      <button data-testid="staff-payment-add-button" className={`${btnPrimary} w-full`}><Plus className="h-4 w-4" /> Ödeme Ekle</button>
    </form>
  );
}

function Totals({ salary, t }) {
  const items = [["Maaş", tl(salary), "text-slate-900"], ["Avans", tl(t.avans), "text-amber-700"], ["Maaş Ödemesi", tl(t.maas), "text-rose-700"], ["Kalan", tl(t.remaining), t.remaining < 0 ? "text-rose-700" : "text-emerald-700"]];
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      {items.map(([l, v, c]) => (
        <div key={l} className="rounded-xl bg-slate-50 p-2.5" data-testid={`staff-total-${l}`}>
          <div className="text-[11px] font-semibold uppercase text-slate-500">{l}</div>
          <div className={`font-mono text-sm font-bold ${c}`}>{v}</div>
        </div>
      ))}
    </div>
  );
}

function StaffProfile({ bid, staff, onClose, onChanged }) {
  const [month, setMonth] = useState(monthStr());
  const [d, setD] = useState(null);
  const load = useCallback(async () => {
    if (staff) setD((await api.get(`/businesses/${bid}/staff/${staff.id}/payments`, { params: { month } })).data);
  }, [bid, staff, month]);
  useEffect(() => {
    load();
  }, [load]);
  const run = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      load();
      onChanged();
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    }
  };
  return (
    <Dialog open={!!staff} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] w-[calc(100%-1.5rem)] max-w-lg overflow-y-auto rounded-2xl" data-testid="staff-profile-dialog">
        <DialogHeader><DialogTitle className="font-heading">{staff?.first_name} {staff?.last_name}</DialogTitle></DialogHeader>
        <Input data-testid="staff-profile-month-input" type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className="h-10 w-44 rounded-xl" />
        {d && <Totals salary={d.staff.salary} t={d.totals} />}
        <PaymentForm onAdd={(body) => run(() => api.post(`/businesses/${bid}/staff/${staff.id}/payments`, body), "Ödeme eklendi")} />
        <ul className="divide-y divide-slate-100" data-testid="staff-payments-list">
          {d?.items.length === 0 && <li className="py-4 text-center text-sm text-slate-400">Bu ay ödeme yok</li>}
          {d?.items.map((p) => (
            <li key={p.id} className="flex items-center gap-3 py-2.5" data-testid={`staff-payment-${p.id}`}>
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${p.pay_type === "avans" ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-700"}`}>{p.pay_type === "avans" ? "Avans" : "Maaş"}</span>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-slate-800">{shortDate(p.date)} {p.note && <span className="text-xs text-slate-500">· {p.note}</span>}</div>
                <div className="text-[11px] text-slate-500">{p.source === "kasa" ? "Günlük defterden (kasa)" : "Kasa dışı"}</div>
              </div>
              <span className="font-mono text-sm font-semibold">{tl(p.amount)}</span>
              <button data-testid={`staff-payment-delete-${p.id}`} onClick={() => window.confirm("Ödeme silinsin mi?") && run(() => api.delete(`/businesses/${bid}/staff/${staff.id}/payments/${p.id}`), "Ödeme silindi")}
                className="rounded-lg p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}

function StaffCard({ s, onOpen, onEdit, onDelete }) {
  return (
    <div className={`${card} space-y-3`} data-testid={`staff-card-${s.id}`}>
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 to-blue-900 text-white"><UserRound className="h-5 w-5" /></span>
        <div className="min-w-0 flex-1">
          <div className="truncate font-semibold text-slate-900">{s.first_name} {s.last_name}</div>
          <div className="flex items-center gap-1 text-xs text-slate-500"><Phone className="h-3 w-3" /> {s.phone || "—"}</div>
        </div>
        <button data-testid={`staff-edit-${s.id}`} onClick={onEdit} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100"><Pencil className="h-4 w-4" /></button>
        <button data-testid={`staff-delete-${s.id}`} onClick={onDelete} className="rounded-lg p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
      </div>
      <Totals salary={s.salary} t={s.totals} />
      <button data-testid={`staff-open-${s.id}`} onClick={onOpen} className="flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
        <Wallet className="h-4 w-4" /> Profil & Ödemeler
      </button>
    </div>
  );
}

export default function Staff() {
  const { current } = useBusiness();
  const [list, setList] = useState([]);
  const [dlg, setDlg] = useState({ open: false, initial: null });
  const [profile, setProfile] = useState(null);

  const load = useCallback(async () => {
    if (current) setList((await api.get(`/businesses/${current.id}/staff`)).data);
  }, [current]);
  useEffect(() => {
    load();
  }, [load]);

  const save = async (body) => {
    try {
      if (dlg.initial) await api.put(`/businesses/${current.id}/staff/${dlg.initial.id}`, body);
      else await api.post(`/businesses/${current.id}/staff`, body);
      toast.success("Personel kaydedildi");
      setDlg({ open: false, initial: null });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const del = async (s) => {
    if (!window.confirm(`${s.first_name} ${s.last_name} silinsin mi? (Kasa dışı ödemeleri de silinir)`)) return;
    await api.delete(`/businesses/${current.id}/staff/${s.id}`);
    toast.success("Personel silindi");
    load();
  };

  return (
    <div className="space-y-6" data-testid="staff-page">
      <PageHeader title="Personel" subtitle="Personel bilgileri, bu ayki avans ve maaş ödemeleri.">
        <button data-testid="add-staff-button" onClick={() => setDlg({ open: true, initial: null })} className={btnPrimary}><Plus className="h-4 w-4" /> Personel Ekle</button>
      </PageHeader>
      {list.length === 0 && <div className={`${card} text-center text-sm text-slate-500`}>Henüz personel eklenmedi.</div>}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {list.map((s) => (
          <StaffCard key={s.id} s={s} onOpen={() => setProfile(s)} onEdit={() => setDlg({ open: true, initial: s })} onDelete={() => del(s)} />
        ))}
      </div>
      <StaffDialog {...dlg} onClose={() => setDlg({ open: false, initial: null })} onSave={save} />
      {current && <StaffProfile bid={current.id} staff={profile} onClose={() => setProfile(null)} onChanged={load} />}
    </div>
  );
}
